import { ApolloLink, Observable } from '@common/hooks/graphql';
import { metrics } from '@sentry/react';

export const GRAPHQL_OUTCOME_METRIC = 'browser.graphql.operations';

type Outcome =
  | 'ok'
  | 'network_error'
  | 'http_error'
  | 'graphql_error'
  | 'cancelled';

function errorOutcome(error: unknown): Outcome {
  if (error instanceof Error && error.name === 'AbortError') return 'cancelled';
  if (error && typeof error === 'object' && 'statusCode' in error)
    return 'http_error';
  return 'network_error';
}

export function createSentryGraphqlOutcomeLink({
  enabled,
  samplePercent = 0,
}: {
  enabled: boolean;
  samplePercent?: number;
}) {
  const state = {
    active:
      enabled &&
      Number.isFinite(samplePercent) &&
      samplePercent > 0 &&
      samplePercent <= 100 &&
      Math.random() * 100 < samplePercent,
  };

  return new ApolloLink((operation, forward) => {
    if (!state.active) return forward(operation);

    return new Observable((observer) => {
      const result: { finished: boolean; outcome: Outcome } = {
        finished: false,
        outcome: 'ok',
      };

      function finish(outcome: Outcome) {
        if (result.finished) return;
        result.finished = true;
        if (!state.active) return;
        try {
          const definition = operation.query.definitions.find(
            (item) => item.kind === 'OperationDefinition'
          );
          metrics.count(GRAPHQL_OUTCOME_METRIC, 1, {
            attributes: { outcome, operation_type: definition?.operation },
          });
        } catch {
          state.active = false;
        }
      }

      const subscription = forward(operation).subscribe({
        next(value) {
          try {
            if (value.errors?.length) result.outcome = 'graphql_error';
          } catch {
            state.active = false;
          }
          observer.next(value);
        },
        error(error) {
          try {
            finish(errorOutcome(error));
          } catch {
            state.active = false;
          }
          observer.error(error);
        },
        complete() {
          finish(result.outcome);
          observer.complete();
        },
      });

      return () => {
        finish('cancelled');
        subscription.unsubscribe();
      };
    });
  });
}
